import React from 'react';
import { TreeSelect, Tag } from 'antd';

import { FormattedMessage } from 'react-intl';
import { useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';
import SurveyGroupStatusTag from 'components/SurveyGroupStatusTag';

import globalMessages from 'translations/messages';
import SurveyTitle from 'components/SurveyTitle';
import useFormatMessage from 'hooks/useFormatMessage';
import messages from './messages';

const SURVEY_GROUPS_QUERY = gql`
  query($customerIds: [ID!], $websiteIds: [ID!], $dataSourceId: ID, $type: SurveyType) {
    surveyGroups(customerIds: $customerIds, websiteIds: $websiteIds, dataSourceId: $dataSourceId, type: $type) {
      id
      type
      title
      surveys {
        id
        title
        version
        language
        languageName
        distributions {
          id
        }
      }
    }
  }
`;

function findNodes(tree, id, nodes = []) {
  const ids = Array.isArray(id) ? id : [id];

  tree.forEach(n => {
    if (ids.includes(n.id)) {
      nodes.push(n);
    }

    if (n.children?.length > 0) {
      findNodes(n.children, ids, nodes);
    }
  });

  if (Array.isArray(id)) {
    return nodes;
  }
  return nodes.length > 0 ? nodes[0] : null;
}

const SurveyGroupSelect = ({
  surveyGroups,
  customerId,
  dataSourceId,
  websiteIds,
  type,
  value,
  onChange,
  exclude = [],
  selectables = ['group', 'survey'],
  valueAsObject = false,
  showChildren = true,
  showStatus = true,
  ...rest
}) => {
  const $t = useFormatMessage();
  const { loading, data } = useQuery(SURVEY_GROUPS_QUERY, {
    variables: { customerIds: [customerId], websiteIds, dataSourceId, type },
    skip: surveyGroups || (!customerId && !websiteIds && !dataSourceId),
  });

  const toSurveyNode = (survey, group, nodeValue) => ({
    id: survey.id,
    value: nodeValue,
    title: (
      <>
        {showStatus && <SurveyGroupStatusTag surveyGroup={group} distributions={survey.distributions} showBadge />}
        <SurveyTitle survey={survey} />
      </>
    ),
    plainTitle: survey.title,
    selectable: selectables.includes('survey'),
    disabled: !selectables.includes('survey'),
    survey,
    group,
  });

  // Fix for changing id of surveygroups from backend
  let valueInTree = valueAsObject ? value?.survey?.id : value;

  const tree = (surveyGroups || data?.surveyGroups)?.filter(g => !exclude.includes(g.id)).map((group) => {
    if (group.type === 'SINGLE') {
      if (group.surveys[0].id === value) {
        valueInTree = group.id;
      }
      return toSurveyNode(group.surveys[0], group, group.id);
    }

    const children = showChildren && group.surveys.filter(s => !exclude.includes(s.id)).map(s => toSurveyNode(s, group, s.id));

    return {
      id: group.id,
      title: (
        <>
          {showStatus && <SurveyGroupStatusTag surveyGroup={group} distributions={group.surveys.flatMap(s => s.distributions || [])} showBadge />}
          <>
            <Tag><FormattedMessage {...messages.group} /></Tag>
            {group.title}
          </>
        </>
      ),
      plainTitle: group.title,
      value: group.id,
      selectable: selectables.includes('group'),
      disabled: !selectables.includes('group'),
      children,
    };
  });

  const handleChange = (v) => {
    if (valueAsObject) {
      onChange(findNodes(tree, v));
    } else {
      onChange(v);
    }
  };

  // Set value when loading is done, to force the select to render the title.
  // Otherwize it rendered the value (id) and didn't change even after
  // loading and setting treeData.
  // This however gives us an oppertunity to display a loading text while loading :)
  // const realValue = valueInTree;

  if (loading) {
    if (rest.treeCheckable) {
      valueInTree = [$t(messages.loading)];
    } else {
      valueInTree = $t(messages.loading);
    }
  }

  return (
    <TreeSelect
      virtual={false}
      disabled={rest.loading || loading}
      placeholder={$t(globalMessages.selectSurvey)}
      treeData={tree}
      value={valueInTree}
      onChange={handleChange}
      showSearch
      treeNodeFilterProp="plainTitle"
      treeDefaultExpandAll
      {...rest}
    />
  );
};

export default SurveyGroupSelect;
